import React, { useState, useContext, useRef, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Dropdown from "../Dropdown/Dropdown";
import RTDLogo from "../RTDLogo";
import { AuthContext } from "../../utils/AuthContext";
import { useFetchBusDivisions } from "../../hooks/use-fetch-bus-divisions";
import handleLogout from "../../utils/handleLogout";
import { useBannerStore } from "../../stores/bannerStore";
import { getPriorityConfig } from "../../utils/bannerMessages";
import "./Navbar.css";

const Navbar = () => {
  const [dropdown, setDropdown] = useState(false);
  const selectorRef = useRef(null);
  const {
    userDivisions,
    operatorView,
    operatorDivisions,
    setOperatorDivisions,
    operatorDivision,
    setOperatorDivision,
    getCurrentDivision,
  } = useContext(AuthContext);
  const accessGranted = userDivisions.length > 0;
  const bannerDismissed = useBannerStore((state) => state.dismissed);
  const bannerCount = useBannerStore((state) => state.count);
  const bannerTopPriority = useBannerStore((state) => state.topPriority);
  const restoreBanner = useBannerStore((state) => state.restore);
  const showAlertsPill = bannerDismissed && bannerCount > 0;
  const alertTone = getPriorityConfig(bannerTopPriority);
  const fetchBusDivisions = useFetchBusDivisions();

  const pathname = window.location.pathname;
  const textAfterSlash = pathname.substring(pathname.lastIndexOf("/") + 1);

  let headerText = "Bus Vehicle Assignments";
  if (textAfterSlash === "FixedRoute") {
    headerText = "Fixed Route Bus Assignments";
  }
  if (textAfterSlash === "SpecialServices") {
    headerText = "Special Services Bus Assignments";
  }

  // Fetch operator divisions when in operator view
  const getDivisions = useCallback(async () => {
    const response = await fetchBusDivisions("operator-view");
    if (response.success) {
      setOperatorDivisions(response.data);
      const hasRequestedDivision = operatorDivision !== "0" &&
        response.data.some(({ divisionId }) => String(divisionId) === String(operatorDivision));
      if (!hasRequestedDivision) {
        setOperatorDivision(response.data[0].divisionId);
      }
    }
  }, [fetchBusDivisions, setOperatorDivisions, setOperatorDivision, operatorDivision]);

  useEffect(() => {
    if (operatorView) {
      getDivisions();
    }
  }, [operatorView, getDivisions]);

  // Clear division param from URL after initial load and persist selection in sessionStorage
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (operatorView && searchParams.has("division")) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("division");
      setSearchParams(newParams, { replace: true });
    }
  }, [operatorView, searchParams, setSearchParams]);

  useEffect(() => {
    if (operatorView && operatorDivision !== "0") {
      sessionStorage.setItem("operatorDivision", operatorDivision);
    }
  }, [operatorView, operatorDivision]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav className="navbar">
      <div className="navbar-left">
        <Link to="/" className="navbar-logo-link" aria-label="Home">
          <RTDLogo />
        </Link>

        <div className="navbar-divider" />

        {operatorView && (
          <>
            <span className="navbar-title">View Bus Assignments</span>
            {operatorDivisions.length > 0 && (
              <>
                <div className="navbar-divider" />
                <div className="nav-division-selector">
                  <label htmlFor="navDivisionSelect" className="nav-division-label">
                    Division
                  </label>
                  <select
                    id="navDivisionSelect"
                    className="nav-division-select"
                    value={operatorDivision}
                    onChange={(e) => setOperatorDivision(e.target.value)}
                  >
                    {operatorDivisions.map(({ divisionId, divisionName }) => (
                      <option key={divisionId} value={divisionId}>
                        {divisionName}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </>
        )}

        {!operatorView && accessGranted && (
          <>
            <div className="nav-page-selector" ref={selectorRef}>
              <button
                className="nav-page-button"
                onClick={() => setDropdown(!dropdown)}
                aria-expanded={dropdown}
                aria-haspopup="true"
              >
                <span>{headerText}</span>
                <i
                  className={`fas fa-chevron-down nav-chevron ${dropdown ? "nav-chevron--open" : ""}`}
                />
              </button>
              {dropdown && <Dropdown setDropdown={setDropdown} />}
            </div>
            <div className="navbar-divider" />
            <button
              className="nav-operator-view-link"
              onClick={() => {
                const division = getCurrentDivision();
                const base = process.env.REACT_APP_ROUTER_BASE || "";
                const divParam = division === "0" ? "" : `&division=${division}`;
                globalThis.open(`${base}/?operator-view=true${divParam}`, "_blank", "noopener,noreferrer");
              }}
            >
              Operator View{" "}
              <i className="fas fa-external-link-alt nav-operator-view-icon" />
            </button>
          </>
        )}

        {!operatorView && !accessGranted && (
          <span className="navbar-title">Bus Vehicle Assignments</span>
        )}
      </div>

      <div className="navbar-right">
        {showAlertsPill && (
          <button
            type="button"
            className="nav-alerts-pill"
            style={{
              backgroundColor: alertTone.fill,
              borderColor: alertTone.rule,
              color: alertTone.text,
            }}
            onClick={restoreBanner}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
            </svg>
            {`${bannerCount} Alert${bannerCount === 1 ? "" : "s"}`}
          </button>
        )}
        {!operatorView && accessGranted && (
          <button className="nav-logout" onClick={handleLogout}>
            Logout
          </button>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
